import { EventProductCard, LoadingProductCard } from '@account/components';
import { useAttendedEventsContext } from '@account/contexts';
import { PaginationReturn } from '@common/types/service';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { useLazyAllPublicEventsQuery } from '@event/services';
import { RegistrationEventListItem } from '@event/types';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { Grid2, useMediaQuery } from '@mui/material';
import { handleError } from '@utils/common';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const filtersAndOrdering = {
    filters: {
        present: ['yes'],
        /**
         * This endpoint is by default only returning events that are ongoing or upcoming.
         * To also include past events (which is necessary to show attended events), we need to send date_end= as a filter.
         */
        date_end: ['blank'],
    },
    ordering: '-date_start',
};

export const AttendedEventsDesktop = () => {
    const intl = useIntl();
    const { downloadingCertificateKey, handleDownloadCertificate } = useAttendedEventsContext();

    //#region Pagination and Data
    const isLg = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const isMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const isSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const pageSize = isLg ? 16 : isMd ? 12 : isSm ? 8 : 4;

    const scrollElement = useMaxWidthWrapperScrollElement();

    const [loadEventsTrigger, { isError }] = useLazyAllPublicEventsQuery();

    const loadEventsPage = useCallback(
        async ({ page, pageSize: nextPageSize }: { page: number; pageSize: number }) => {
            const pageDataResponse = await loadEventsTrigger(
                {
                    ...filtersAndOrdering,
                    page,
                    page_size: nextPageSize,
                },
                true
            ).unwrap();

            return pageDataResponse as PaginationReturn<RegistrationEventListItem, number>;
        },
        [loadEventsTrigger]
    );

    const { items, sentinelRef, hasRealItems, isLoadingMore } = useInfiniteLazyLoader<RegistrationEventListItem>({
        pageSize,
        scrollElement,
        contextKey: `attended-events:${pageSize}`,
        loadPage: loadEventsPage,
        getItemKey: (event) => `${event.type}-${event.id}`,
        onError: (error) => {
            console.error('Error loading events:', error);
            handleError('Error loading events', error);
        },
    });

    //#endregion

    return (
        <Grid2 container spacing={2} pb="24px">
            {hasRealItems || isLoadingMore ? (
                items.map((event, index) => {
                    if (isInfiniteLoadingItem(event)) {
                        return (
                            <Grid2 key={event.name} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                <LoadingProductCard key={`skeleton-${index}`} intl={intl} />
                            </Grid2>
                        );
                    }

                    return (
                        <Grid2 key={`event-${event.type}-${event.id}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <EventProductCard
                                event={event}
                                onDownloadCertificate={() => handleDownloadCertificate(event)}
                                isCertificatesDownloading={downloadingCertificateKey[`${event.type}-${event.id}`]}
                            />
                        </Grid2>
                    );
                })
            ) : isError ? (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="account.events.error"
                            defaultMessage="An error occurred while fetching your attended events."
                        />
                    }
                />
            ) : (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="account.events.noAttendedEvents"
                            defaultMessage="You haven't attended any events yet."
                        />
                    }
                />
            )}
            <div ref={sentinelRef} />
        </Grid2>
    );
};
